/* eslint-disable no-script-url */
import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { MARKET, VEHICLE_FALLBACK_IMAGE as vehicleFallbackImage } from '../../constants';
import { FilteredVehicle, GalleryImagesType } from '../../types';
import Modal from '../Modal';
import Location from './Location';
import {
  ImageContainer,
  Button,
  VehicleInfo,
  VehicleName,
  VehicleTrim,
  CpoBLOCK,
  CpoIcon,
  Milage,
  Legal,
  TextContainer,
  SingleVehicleImage,
  StatusContainer,
} from './VehicleCard.styles';
import CPOLogo from '../../assets/images/CertifiedBadges/CPO';
import Prices from './Prices';
import ImagesGallery from '../ImagesGallery/ImagesGallery';
import Status from './Status';
import CtaGroup from './CtaGroup/CtaGroup';
import { CarType } from '../../types/oneGraphTypes';

interface VehicleCardProps {
  vehicle: CarType;
  marketType: string;
  vehicleDetailsUrl: string;
  isImageGalleryOn: boolean;
}

const T1VehicleCard: React.FC<VehicleCardProps> = ({
  vehicle,
  marketType,
  isImageGalleryOn,
  vehicleDetailsUrl,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.languages?.[0] || 'en';
  const isEnglish = i18n.language === 'en';

  const { stockCar, geoDistance } = vehicle;
  const {
    id,
    cartypeText,
    salesInfo,
    dealer,
    carPrices,
    images,
    titleText,
    mileage,
    model,
    subtitleText,
    qualityLabel,
  } = stockCar;
  const { orderStatusText, availableFromDateInfo } = salesInfo;
  const { salesModelyear, name } = model;
  const manufacturerSpecificItems = stockCar?.manufacturerSpecificItems;

  const isMarketCA = marketType === 'CAN';
  const isNewVehicle = cartypeText === 'N';
  const isAvailableNow = availableFromDateInfo.value === 'Immediately';

  const isCPO = !!(!isNewVehicle && qualityLabel.length && qualityLabel[0].label === 'CPO');

  const isReservedVehicle = salesInfo.saleOrderTypeText?.includes('SOLD');
  const modelImageFromScs = images[0]?.url;
  const modelMileage = mileage.value.formatted;
  const bodyType = useMemo(() => {
    const bodyInfo = manufacturerSpecificItems?.cdbItems?.find((item) => item?.id === 'body-type');
    if (bodyInfo?.value?.length) return bodyInfo?.value;
    return '';
  }, [manufacturerSpecificItems?.cdbItems]);

  const modelPrice = useMemo(() => {
    const retailPrice = carPrices?.find((price) => price.type === 'sale')?.price;
    const regularPrice = carPrices?.find((price) => price.type === 'list')?.price;
    // US and CA Pre-Owned
    if (!isNewVehicle) {
      if (retailPrice?.value && retailPrice.formattedValue) return retailPrice.formattedValue;
      return t('vlp.vehicleResults.vehicleCard.priceDefaultAvailableSoon');
    }
    // CA NEW
    if (isMarketCA && isNewVehicle) {
      if (retailPrice?.value && retailPrice.formattedValue) return retailPrice.formattedValue;
      return t('vlp.vehicleResults.vehicleCard.priceDefaultAvailableSoon');
    }
    // US NEW
    if (!isMarketCA && isNewVehicle) {
      if (regularPrice?.value && regularPrice.formattedValue) return regularPrice.formattedValue;
      return t('vlp.vehicleResults.vehicleCard.priceDefaultAvailableSoon');
    }
    return '';
  }, [carPrices, isMarketCA, isNewVehicle, t]);

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const digitalLeadsData = JSON.stringify({
    vehicleId: id,
    vehicleType: isNewVehicle ? 'new' : 'used',
    market: marketType,
    presetForm: 'contact-dealer',
    dealerId: dealer.id.slice(3),
  });

  const onModalOpen = useCallback((e: any, title?: string, text?: string | JSX.Element) => {
    e.preventDefault();
    e.stopPropagation();
    setModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const isQ5Hybrid = name.includes('Q5') && name.includes('TFSI e');
  const modelName = isQ5Hybrid ? `${titleText} Plug-in Hybrid` : titleText;

  const formattedPrice = useMemo(() => {
    const isMarketUSA = marketType === 'USA';
    // let priceFormatted = `${correctPrice.replace(' ', '')}`;
    let priceFormatted = modelPrice;

    if (!isMarketUSA && !isEnglish && modelPrice) {
      priceFormatted = `${modelPrice.replace('$', '').replace(',', ' ').replace('.', ',')} $ CA`;
    }

    return priceFormatted;
  }, [modelPrice, isEnglish, marketType]);

  const popoverDescription = useMemo(() => {
    if (isReservedVehicle) return t('vlp.vehicleResults.vehicleCard.reserved');
    if (isAvailableNow && isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableNowCAN.popoverDescription');
    if (isAvailableNow && !isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableNowUSA.popoverDescription');
    if (!isAvailableNow && isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableSoon.popoverDescriptionCAN');
    return t('vlp.vehicleResults.vehicleCard.availableSoon.popoverDescriptionUSA');
  }, [isAvailableNow, isMarketCA, isReservedVehicle, t]);

  const altFunction = useCallback(() => {
    if (modelImageFromScs && modelImageFromScs.includes('default-images')) {
      return t('vlp.vehicleResults.vehicleCard.vehicleFallbackAlt', { titleText });
    }
    return t('vlp.vehicleResults.vehicleCard.vehicleStandardAlt', { titleText });
  }, [modelImageFromScs, titleText, t]);

  const statusForDot = useMemo(() => {
    if (isReservedVehicle) return 'RESERVED';
    if (isAvailableNow) return 'AVAILABLE';
    return 'PIPELINE';
  }, [isAvailableNow, isReservedVehicle]);

  const vehicleStatus = useMemo(() => {
    if (isReservedVehicle) return 'Reserved';
    if (isAvailableNow)
      return t(`vlp.vehicleResults.vehicleCard.availableNow${MARKET}.availabilityLabel`);
    if (!isAvailableNow && isNewVehicle && orderStatusText === 'Intransit From Factory')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.factory');
    if (!isAvailableNow && isNewVehicle && orderStatusText === 'Port Stock')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.port');
    if (!isAvailableNow && isNewVehicle && orderStatusText === 'Intransit Inland')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.inland');
    if (!isAvailableNow && isNewVehicle && orderStatusText == null)
      return t('vlp.vehicleResults.vehicleCard.orderStatus.contactDealer');

    return '';
  }, [isReservedVehicle, isAvailableNow, t, isNewVehicle, orderStatusText]);

  const priceLegalText = useMemo(() => {
    if (marketType === 'CAN') return t('vlp.vehicleResults.vehicleCard.dealerListPriceLegalText');

    if (
      marketType === 'USA' &&
      isNewVehicle &&
      (salesModelyear === 2025 || salesModelyear === 2026)
    )
      return t('vlp.vehicleResults.vehicleCard.msrpLegalTextUSA-2025');

    if (marketType === 'USA' && isNewVehicle)
      return t('vlp.vehicleResults.vehicleCard.msrpLegalTextUSA');

    // marketType === 'USA' && vehicleType === 'used'
    return (
      <Trans
        i18nKey="vlp.vehicleResults.vehicleCard.dealerPriceLegalText"
        // the global reset css imported in several feature apps
        // contains globally applied oppinionated styling
        components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
      />
    );
  }, [marketType, isNewVehicle, t, salesModelyear]);

  const galleryImagesVar = useMemo(() => {
    // if there are at least one image in galleryImages array return that
    // otherwise return modelImageFromScs or vehicleFallbackImage
    if (images?.length > 0) return images;
    const modelImage = modelImageFromScs || vehicleFallbackImage;
    const singleImageObject = [
      {
        id: { group: 'renderImagesPNG', image: 'vtp4x3n3c' },
        type: 'render',
        url: modelImage,
      },
    ];
    return singleImageObject;
  }, [images, modelImageFromScs]);

  const modelImage = modelImageFromScs || vehicleFallbackImage;

  const unitOfMeasurement = marketType === 'CAN' ? 'km' : 'miles';

  const renderCpoBlock = useMemo(() => {
    return (
      <CpoBLOCK isCPO={isCPO}>
        {isCPO && (
          <CpoIcon>
            <CPOLogo />
          </CpoIcon>
        )}
        {!isNewVehicle && (
          <Milage data-testid="model-mileage" data-cy="vehicle-card-mileage">
            <Trans
              i18nKey="vlp.vehicleResults.vehicleCard.mileage"
              values={{ modelMileage, unitOfMeasurement }}
              components={{ 0: <br /> }}
            />
          </Milage>
        )}
      </CpoBLOCK>
    );
  }, [isCPO, modelMileage, unitOfMeasurement, isNewVehicle]);

  const renderGallery = useMemo(() => {
    return (
      <ImageContainer>
        {isImageGalleryOn ? (
          <ImagesGallery
            isNewVehicle={isNewVehicle}
            isReservedVehicle={isReservedVehicle}
            images={galleryImagesVar}
            bodyType={bodyType}
            alt={altFunction()}
            data-cy="vehicle-card-image-gallery"
          />
        ) : (
          <SingleVehicleImage
            src={modelImage}
            alt={altFunction()}
            isNewVehicle={isNewVehicle}
            isReservedVehicle={isReservedVehicle}
            data-cy="vehicle-card-simgle-image"
          />
        )}
      </ImageContainer>
    );
  }, [
    altFunction,
    bodyType,
    galleryImagesVar,
    isImageGalleryOn,
    isNewVehicle,
    isReservedVehicle,
    modelImage,
  ]);

  const renderStatus = useMemo(() => {
    return (
      <Status
        popoverDescription={popoverDescription}
        vehicleStatus={vehicleStatus}
        statusForDot={statusForDot}
      />
    );
  }, [popoverDescription, statusForDot, vehicleStatus]);

  const renderCardContent = useMemo(() => {
    return (
      <>
        <VehicleInfo>
          {isNewVehicle && <StatusContainer>{renderStatus}</StatusContainer>}
          <VehicleName data-testid="model-name" data-cy="vehicle-card-model-name">
            {modelName}
          </VehicleName>
          <VehicleTrim data-testid="trim-name" data-cy="vehicle-card-trim-name">
            {subtitleText}
          </VehicleTrim>
          <Location
            dealerName={dealer.name}
            unitOfMeasurement={unitOfMeasurement}
            distanceFromUser={geoDistance.value.number}
            isNewVehicle={isNewVehicle}
          />
          {!isNewVehicle && renderCpoBlock}
        </VehicleInfo>
        {renderGallery}
        <Prices
          modelPrice={modelPrice ?? ''}
          vehicleType={isNewVehicle ? 'new' : 'used'}
          isAvailableNow={isAvailableNow}
          marketType={marketType}
          onModalOpen={onModalOpen}
        />
        <CtaGroup vehicleDetailsUrl={vehicleDetailsUrl} digitalLeadsData={digitalLeadsData} />
      </>
    );
  }, [
    isNewVehicle,
    renderStatus,
    modelName,
    subtitleText,
    dealer.name,
    unitOfMeasurement,
    geoDistance.value.number,
    renderCpoBlock,
    renderGallery,
    modelPrice,
    isAvailableNow,
    marketType,
    onModalOpen,
    vehicleDetailsUrl,
    digitalLeadsData,
  ]);

  if (
    // If we get null, we hide it
    isAvailableNow === null ||
    // if availableNow is boolean false and
    // vehicleOrderStatus not one of the below three, we hide it
    (!isAvailableNow &&
      orderStatusText &&
      !['Intransit From Factory', 'Port Stock', 'Intransit Inland'].includes(orderStatusText))
    // if availableNow is boolean false and
    // saleOrderType not the one below, we hide it
    // (!isAvailableNow &&
    //   saleOrderType &&
    //   !['SHIP - Deliverable Inventory Order'].includes(saleOrderType))
  )
    return null;

  return (
    <>
      <Button isReservedVehicle={isReservedVehicle} data-cy="vehicle-card-button" noCursor>
        {renderCardContent}
      </Button>

      <Modal isOpen={modalOpen} onClose={onModalClose} data-cy="vehicle-card-legal-modal">
        <Legal>
          <TextContainer>{priceLegalText}</TextContainer>
        </Legal>
      </Modal>
    </>
  );
};

export default T1VehicleCard;
